import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { AlertTriangle, CalendarDays, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { importHolidays } from "@/lib/api/admin-time-off";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";

// Curated short list — admins can override by typing any ISO-3166-1 alpha-2 code.
const COMMON_COUNTRIES = ["PL", "US", "GB", "DE", "FR", "ES", "IT", "NL", "SE", "NO", "DK", "FI"];

function currentYear() {
  return DateTime.now().year;
}

export function ImportHolidaysDialog({
  open,
  onOpenChange,
  tenantId,
  defaultCountry,
  onImported,
}) {
  const [country, setCountry] = useState((defaultCountry || "").toUpperCase());
  const [year, setYear] = useState(currentYear());
  const [previewItems, setPreviewItems] = useState([]);
  const [skipped, setSkipped] = useState(() => new Set());
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null); // { fetched, country, year }

  useEffect(() => {
    if (open) {
      setCountry((defaultCountry || "").toUpperCase());
      setYear(currentYear());
      setPreviewItems([]);
      setSkipped(new Set());
      setPreviewError(null);
      setPreviewMeta(null);
    }
  }, [open, defaultCountry]);

  const canPreview = Boolean(tenantId) && country.trim().length === 2 && Number.isInteger(year) && year >= 1900;

  const runPreview = async () => {
    if (!canPreview || previewLoading) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const response = await importHolidays({
        tenantId,
        country: country.trim().toUpperCase(),
        year,
        dryRun: true,
      });
      const items = Array.isArray(response?.items) ? response.items : [];
      setPreviewItems(items);
      setSkipped(new Set());
      setPreviewMeta({
        fetched: response?.fetched ?? items.length,
        country: response?.country || country,
        year: response?.year || year,
      });
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Failed to fetch holidays.");
      setPreviewItems([]);
      setPreviewMeta(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleSkip = (localKey) => {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(localKey)) next.delete(localKey);
      else next.add(localKey);
      return next;
    });
  };

  const runImport = async () => {
    if (importLoading || previewItems.length === 0) return;
    setImportLoading(true);
    try {
      const skipLocalKeys = Array.from(skipped);
      const response = await importHolidays({
        tenantId,
        country: country.trim().toUpperCase(),
        year,
        dryRun: false,
        skipLocalKeys,
      });
      const created = response?.created || 0;
      const updated = response?.updated || 0;
      const skippedCustomized = response?.skippedCustomized || 0;
      const skippedSoftDelete = response?.skippedSoftDelete || 0;
      const totalApplied = created + updated;
      toast.success(
        `Imported ${totalApplied} holiday${totalApplied === 1 ? "" : "s"} for ${country} ${year}` +
          (skippedCustomized || skippedSoftDelete
            ? ` · ${skippedCustomized + skippedSoftDelete} skipped (customized or removed)`
            : ""),
      );
      publishCalendarRefresh({ source: "time-off-import" });
      if (typeof onImported === "function") onImported(response);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed.");
    } finally {
      setImportLoading(false);
    }
  };

  const selectedCount = previewItems.length - skipped.size;
  const importDisabled = !previewMeta || selectedCount <= 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !importLoading && onOpenChange?.(next)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="size-5 text-primary" aria-hidden />
            Import public holidays
          </DialogTitle>
          <DialogDescription>
            Fetches the official public-holiday list for a country and year from the Nager.Date
            provider. Imported holidays are global — they apply to every tenant in the matching
            country and aren't owned by any single tenant. Re-importing the same year is safe —
            already-imported entries are updated, manually-edited or removed entries are kept as-is.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Country (ISO-3166-1 alpha-2)</label>
              <div className="flex gap-2">
                <Input
                  value={country}
                  onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 2))}
                  placeholder="PL"
                  className="h-9 w-20 font-mono uppercase"
                  maxLength={2}
                />
                <div className="flex flex-wrap gap-1">
                  {COMMON_COUNTRIES.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setCountry(code)}
                      className={cn(
                        "rounded-md border px-2 py-1 text-xs font-medium transition-colors",
                        country === code
                          ? "border-primary/40 bg-primary/10 text-foreground"
                          : "border-border bg-background text-muted-foreground hover:bg-muted",
                      )}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Year</label>
              <Input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value || "0", 10) || currentYear())}
                min={1900}
                max={2200}
                className="h-9"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={runPreview}
              disabled={!canPreview || previewLoading}
              className="h-8 gap-1.5"
            >
              {previewLoading ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="size-3.5" aria-hidden />
              )}
              {previewMeta ? "Refresh preview" : "Preview"}
            </Button>
            {previewMeta ? (
              <span className="text-xs text-muted-foreground">
                {previewMeta.fetched} holiday{previewMeta.fetched === 1 ? "" : "s"} fetched for{" "}
                {previewMeta.country} {previewMeta.year}
              </span>
            ) : null}
          </div>

          {previewError ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <div>
                <p className="font-medium">Preview failed</p>
                <p className="mt-1 text-xs leading-relaxed">{previewError}</p>
              </div>
            </div>
          ) : null}

          {previewItems.length > 0 ? (
            <ScrollArea className="h-72 rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="w-10 px-3 py-2 text-left">Use</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Holiday</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewItems.map((item) => {
                    const localKey = item?.importRef?.localKey || `${item.date}|${item.title}`;
                    const isSkipped = skipped.has(localKey);
                    return (
                      <tr key={localKey} className="border-t border-border/60 hover:bg-muted/30">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={!isSkipped}
                            onChange={() => toggleSkip(localKey)}
                            aria-label={`Include ${item.title}`}
                            className="size-4"
                          />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{item.date}</td>
                        <td className="px-3 py-2 font-medium text-foreground">{item.title}</td>
                        <td className="px-3 py-2">
                          {isSkipped ? (
                            <Badge variant="secondary" className="text-[10px]">
                              Skipped
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">
                              Will import
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollArea>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={importLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={runImport}
            disabled={importDisabled || importLoading}
            aria-busy={importLoading}
          >
            {importLoading ? <Spinner className="mr-2 size-4" /> : null}
            Import {selectedCount} holiday{selectedCount === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
